import React from 'react';
import {
  Button,
  HStack,
  Textarea,
  Box,
  VStack,
  Text,
  Badge,
  Tooltip,
  Spinner,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, WarningIcon, InfoIcon } from '@chakra-ui/icons';

const ActionButtons = ({
  selectedFileIds,
  selectedFiles,
  hasUnsavedChanges,
  approveLoading,
  rejectLoading,
  showRejectInput,
  rejectionComment,
  onRejectToggle,
  onRejectCommentChange,
  onRejectSubmit,
  onApprove,
  onSave,
  onDiscard,
  isSaving,
  successColor,
  dangerColor,
  validateFilesForAction,
}) => {
  const [rejectConfirmModal, setRejectConfirmModal] = React.useState({
    isOpen: false,
    comment: '',
    fileCount: 0,
  });

  const hasSelection = selectedFileIds && selectedFileIds.length > 0;
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const getActionableFiles = () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      return {
        canApprove: false,
        canReject: false,
        counts: { total: 0, approvable: 0, rejectable: 0, invalid: 0 },
      };
    }

    let approvable = 0;
    let rejectable = 0;
    let invalid = 0;

    selectedFiles.forEach((file) => {
      const status = file.status || 'submitted';
      if (status === 'submitted') {
        approvable++;
        rejectable++; // Allow rejection of submitted files
      } else if (status === 'approved') {
        rejectable++; // Allow rejection of approved files
      } else if (status === 'rejected') {
        invalid++;
      }
    });

    return {
      canApprove: approvable > 0,
      canReject: rejectable > 0,
      counts: {
        total: selectedFiles.length,
        approvable,
        rejectable,
        invalid,
      },
    };
  };

  const actionable = getActionableFiles();

  const getActionTooltip = (action) => {
    if (!hasSelection) return 'Select a Record first';

    if (action === 'approve') {
      if (actionable.counts.invalid === actionable.counts.total) {
        return 'All selected Records are already approved/rejected';
      }
      if (actionable.counts.approvable < actionable.counts.total) {
        return `${actionable.counts.invalid} Record(s) cannot be approved (already approved/rejected)`;
      }
      return 'Approve selected Record';
    } else {
      if (actionable.counts.rejectable === 0) {
        return 'No records available for rejection';
      }
      if (actionable.counts.invalid === actionable.counts.total) {
        return 'All selected Records are already rejected';
      }
      return 'Reject selected Records';
    }
  };

  const handleRejectClick = () => {
    onRejectToggle();
  };

  const handleRejectSubmitClick = () => {
    if (!rejectionComment.trim()) return;

    setRejectConfirmModal({
      isOpen: true,
      comment: rejectionComment,
      fileCount: actionable.counts.rejectable,
    });
  };

  const handleRejectConfirm = () => {
    setRejectConfirmModal({ isOpen: false, comment: '', fileCount: 0 });
    onRejectSubmit();
  };

  const handleRejectCancel = () => {
    setRejectConfirmModal({ isOpen: false, comment: '', fileCount: 0 });
  };

  return (
    <HStack spacing={3} justify="flex-end" w="100%">
      {/* Reject Confirmation Modal */}
      <Modal
        isOpen={rejectConfirmModal.isOpen}
        onClose={handleRejectCancel}
        size="lg"
        isCentered
      >
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader color="red.600" borderBottom="1px" borderColor="gray.200">
            <Flex align="center">
              <WarningIcon color="red.500" mr={2} />
              Confirm Rejection
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={6}>
            <Alert
              status="error"
              variant="left-accent"
              borderRadius="lg"
              mb={4}
            >
              <AlertIcon />
              <Box flex="1">
                <AlertTitle fontSize="md">You are about to reject record</AlertTitle>
                <AlertDescription fontSize="sm">
                  This action cannot be undone. Rejected files will need to be
                  resubmitted by the uploader.
                </AlertDescription>
              </Box>
            </Alert>

            <VStack align="start" spacing={3} mt={4}>
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                  Record to be rejected:
                </Text>
                <Badge colorScheme="red" fontSize="md" px={3} py={1} mt={1}>
                  {rejectConfirmModal.fileCount} record(s)
                </Badge>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={1}>
                  Rejection reason:
                </Text>
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderLeft="4px"
                  borderLeftColor="red.400"
                >
                  <Text fontSize="sm" color="gray.700">
                    "{rejectConfirmModal.comment}"
                  </Text>
                </Box>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter borderTop="1px" borderColor="gray.200">
            <Button
              variant="ghost"
              mr={3}
              onClick={handleRejectCancel}
              isDisabled={rejectLoading}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleRejectConfirm}
              isLoading={rejectLoading}
              loadingText="Rejecting..."
              leftIcon={<WarningIcon />}
            >
              Yes, Reject
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {hasUnsavedChanges && (
        <>
          <Tooltip label="Save all pending changes">
            <Button
              colorScheme="green"
              size="sm"
              leftIcon={<CheckIcon />}
              onClick={onSave}
              isLoading={isSaving}
              loadingText="Saving..."
              boxShadow="md"
              _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
              transition="all 0.2s"
              isDisabled={approveLoading || rejectLoading}
            >
              Save Changes
            </Button>
          </Tooltip>
          <Tooltip label="Discard all pending changes">
            <Button
              colorScheme="red"
              size="sm"
              leftIcon={<CloseIcon />}
              onClick={onDiscard}
              isDisabled={isSaving || approveLoading || rejectLoading}
              variant="outline"
              _hover={{ bg: 'red.50' }}
            >
              Discard
            </Button>
          </Tooltip>
        </>
      )}

      {showRejectInput && (
        <HStack
          spacing={2}
          bg="red.50"
          p={3}
          borderRadius="lg"
          borderWidth="1px"
          borderColor="red.200"
        >
          <Textarea
            placeholder="Enter rejection reason..."
            value={rejectionComment}
            onChange={onRejectCommentChange}
            size="sm"
            rows={1}
            width="250px"
            bg="white"
            borderColor="red.300"
            _focus={{ borderColor: 'red.500', boxShadow: '0 0 0 1px red.500' }}
            isDisabled={rejectLoading || approveLoading}
          />
          <VStack spacing={1} align="stretch">
            <Button
              colorScheme="red"
              size="sm"
              onClick={handleRejectSubmitClick}
              isLoading={rejectLoading}
              loadingText="..."
              isDisabled={
                !rejectionComment.trim() ||
                approveLoading ||
                !actionable.canReject
              }
              leftIcon={<WarningIcon />}
            >
              Submit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={onRejectToggle}
              isDisabled={rejectLoading || approveLoading}
            >
              Cancel
            </Button>
          </VStack>
        </HStack>
      )}

      {!showRejectInput && user?.role === 'schemeAdmin' && (
        <>
          <Tooltip label={getActionTooltip('approve')}>
            <Button
              colorScheme="green"
              size="sm"
              leftIcon={<CheckIcon />}
              onClick={onApprove}
              isLoading={approveLoading}
              isDisabled={
                !hasSelection ||
                hasUnsavedChanges ||
                rejectLoading ||
                !actionable.canApprove
              }
              loadingText="Approving..."
              bg={successColor}
              color="white"
              opacity={!actionable.canApprove && hasSelection ? 0.6 : 1}
              _hover={
                actionable.canApprove
                  ? {
                    bg: 'green.600',
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }
                  : {}
              }
              transition="all 0.2s"
              boxShadow="md"
              px={8}
            >
              Approve
            </Button>
          </Tooltip>

          <Tooltip label={getActionTooltip('reject')}>
            <Button
              colorScheme="red"
              size="sm"
              leftIcon={<CloseIcon />}
              onClick={handleRejectClick}
              isLoading={rejectLoading}
              isDisabled={
                !hasSelection ||
                hasUnsavedChanges ||
                approveLoading ||
                !actionable.canReject
              }
              loadingText="Rejecting..."
              bg={dangerColor}
              color="white"
              opacity={!actionable.canReject && hasSelection ? 0.6 : 1}
              _hover={
                actionable.canReject
                  ? {
                    bg: 'red.600',
                    transform: 'translateY(-2px)',
                    boxShadow: 'lg',
                  }
                  : {}
              }
              transition="all 0.2s"
              boxShadow="md"
              px={8}
            >
              Reject
            </Button>
          </Tooltip>
        </>
      )}

      {hasSelection && !showRejectInput && (
        <HStack spacing={2}>
          {actionable.counts.approvable > 0 &&
            actionable.counts.approvable < actionable.counts.total && (
              <Badge colorScheme="green" px={3} py={1} borderRadius="full">
                {actionable.counts.approvable} Records can approve
              </Badge>
            )}
        </HStack>
      )}
    </HStack>
  );
};

export default ActionButtons;